import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { omit } from 'lodash';
import { forkJoin, map, of, switchMap } from 'rxjs';
import {
  Attachment,
  ClientFeedback,
} from '../../../../team-apis/otterdam/feedback';
import { Project } from '../../../../team-apis/otterdam/project';
import { Task } from '../../../../team-apis/otterdam/task';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { PersistenceService } from '../../misc-services/persistence.service';
import {
  ClientFeedbackRequestOptions,
  ClientFeedbackResource,
  CreateIssueCommentRequestOptions,
  Screenshot,
} from '../../resources/client-issue-resource.service';
import { AbstractFeedbackCrudService } from './abstract-feedback-crud.service';
import {
  buildAttachmentForUpload,
  buildAttachments,
  uploadAttachmentsToS3,
} from './client-issue-crud-helpers';

@Injectable()
export class ClientFeedbackCrudService extends AbstractFeedbackCrudService<ClientFeedback> {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private persistenceService = inject(PersistenceService);
  private clientFeedbackResource = inject(ClientFeedbackResource);
  private httpClient = inject(HttpClient);
  private persistenceQueueName = 'ClientFeedback';

  save(
    clientFeedback: ClientFeedbackRequestOptions,
    screenshots: Screenshot[],
    shouldUpdate = true,
  ) {
    this.addTags(clientFeedback.tags);
    this.persistenceService
      .enqueue$(
        this.createOrUpdateIssue$(screenshots, clientFeedback, shouldUpdate),
        this.persistenceQueueName,
      )
      .subscribe((response) => {
        // TODO: Add a test to make sure issue info is updated correctly
        //  based on the response once POL-363 are implemented
        //  https://bodhi.samasource.org/jira/browse/POL-362
        // fix me if you change this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
        response.comments ??= [];
        this.taskWorkspaceStore('clientFeedback').setUsing(
          (clientFeedbackList) =>
            clientFeedbackList.map((issue) =>
              issue.id === response.id ? response : issue,
            ),
        );
      });
    this.addOrUpdateIssue(clientFeedback, screenshots);
  }

  addOrUpdateIssue(
    clientFeedback: ClientFeedbackRequestOptions,
    screenshots: Screenshot[],
  ) {
    const currentIssueList = this.taskWorkspaceStore.state().clientFeedback;
    const newIssue = {
      ...clientFeedback,
      updatedAt: new Date().toISOString(),
      attachments: buildAttachments(screenshots),
      comments: [],
    };
    let updatedIssuesList: ClientFeedback[];
    if (this.getFeedback(clientFeedback.id)) {
      updatedIssuesList = currentIssueList.map((issue) =>
        issue.id === clientFeedback.id ? { ...issue, ...newIssue } : issue,
      );
    } else {
      updatedIssuesList = [{ ...newIssue, status: 'new' }, ...currentIssueList];
    }
    this.taskWorkspaceStore('clientFeedback').set(updatedIssuesList);
  }

  saveComment(
    comment: CreateIssueCommentRequestOptions,
    issueId: string,
    screenshots: Screenshot[],
  ) {
    const newComment = {
      ...comment,
      createdAt: new Date().toISOString(),
      isSolution: false,
      attachments: buildAttachments(screenshots),
      issueId,
    };
    this.taskWorkspaceStore('clientFeedback').setUsing((clientFeedbackList) =>
      clientFeedbackList.map((issue) =>
        issue.id === issueId
          ? { ...issue, comments: [...issue.comments, newComment] }
          : issue,
      ),
    );
    const { project, task } = this.taskWorkspaceStore.state();
    this.createComment$(screenshots, comment, { project, task, issueId });
  }

  markCommentAsSolution(commentId: string, issueId: string) {
    this.taskWorkspaceStore('clientFeedback').setUsing((clientFeedbackList) =>
      clientFeedbackList.map((issue) =>
        issue.id === issueId
          ? {
              ...issue,
              status: 'closed',
              comments: issue.comments.map((comment) =>
                comment.id === commentId
                  ? { ...comment, isSolution: true }
                  : { ...comment, isSolution: false },
              ),
            }
          : issue,
      ),
    );
    const { project, task } = this.taskWorkspaceStore.state();
    this.persistenceService.enqueue$(
      this.clientFeedbackResource.resolveIssueWithComment$(
        project.id,
        task.id,
        commentId,
        issueId,
      ),
      this.persistenceQueueName,
    );
  }

  override getFeedback(issueId: string) {
    const feedback = this.taskWorkspaceStore.state().clientFeedback;
    return feedback.find(({ id }) => id === issueId);
  }

  override delete(issueId: string) {
    const taskWorkspaceState = this.taskWorkspaceStore.state();

    this.persistenceService.enqueue$(
      this.clientFeedbackResource.deleteIssue$(
        taskWorkspaceState.project.id,
        taskWorkspaceState.task.id,
        issueId,
      ),
      this.persistenceQueueName,
    );
    this.taskWorkspaceStore('clientFeedback').setUsing((clientFeedbackList) =>
      clientFeedbackList.filter((issue) => issue.id !== issueId),
    );
  }

  override getFeedbackOutputId(feedback: ClientFeedback) {
    return feedback.location.output_id;
  }

  override getFeedbackCoordinates(feedback: ClientFeedback) {
    return feedback.location.coordinates;
  }

  private addTags(tags: string[]) {
    this.taskWorkspaceStore('clientFeedbackTags').setUsing(
      (clientFeedbackTags) =>
        Array.from(new Set([...clientFeedbackTags, ...tags])),
    );
  }

  private createOrUpdateIssue$(
    screenshots: Screenshot[],
    clientFeedback: ClientFeedbackRequestOptions,
    shouldUpdate: boolean,
  ) {
    return this.saveAttachments$(screenshots, clientFeedback.id).pipe(
      switchMap(
        (res: Array<{ uploadUrl: string; blob: Blob; body: Attachment }>) => {
          const taskWorkspaceState = this.taskWorkspaceStore.state();
          clientFeedback.attachments = res.map((attachment) => attachment.body);
          uploadAttachmentsToS3(this.httpClient, res);
          if (shouldUpdate) {
            return this.clientFeedbackResource.updateIssue$(
              taskWorkspaceState.project.id,
              taskWorkspaceState.task.id,
              clientFeedback.id,
              omit(clientFeedback, 'id', 'attachments', 'parkTasks'),
            );
          } else {
            return this.clientFeedbackResource.createIssue$(
              taskWorkspaceState.project.id,
              taskWorkspaceState.task.id,
              clientFeedback,
            );
          }
        },
      ),
    );
  }

  private saveAttachments$(
    screenshots: Screenshot[],
    issueId: string,
    isForComment = false,
  ) {
    if (screenshots.length === 0) {
      return of([]);
    }
    return forkJoin(
      screenshots.map((screenshot) => {
        const { project, task } = this.taskWorkspaceStore.state();
        const uploadAttachment = isForComment
          ? this.clientFeedbackResource.uploadAttachmentForComment$.bind(
              this.clientFeedbackResource,
            )
          : this.clientFeedbackResource.uploadAttachmentForClientFeedback$.bind(
              this.clientFeedbackResource,
            );
        return this.persistenceService.enqueue$(
          uploadAttachment(project.id, task.id, screenshot, issueId).pipe(
            map((response) => buildAttachmentForUpload(response, screenshot)),
          ),
          this.persistenceQueueName,
        );
      }),
    );
  }

  private createComment$(
    screenshots: Screenshot[],
    comment: CreateIssueCommentRequestOptions,
    {
      project,
      task,
      issueId,
    }: { project: Project; task: Task; issueId: string },
  ) {
    return this.persistenceService.enqueue$(
      this.saveAttachments$(screenshots, issueId, true).pipe(
        switchMap(
          (res: Array<{ uploadUrl: string; blob: Blob; body: Attachment }>) => {
            comment.attachments = res.map((attachment) => attachment.body);
            uploadAttachmentsToS3(this.httpClient, res);
            return this.clientFeedbackResource.createComment$(
              project.id,
              task.id,
              comment,
              issueId,
            );
          },
        ),
      ),
      this.persistenceQueueName,
    );
  }
}
